import { Box, Stack, styled } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { useGetUploadSummariesQuery } from '../../../../services/entities/impl/blfApi/endpoints/uploadSummaries';
import { NoDataMessage } from '../../components/NoDataMessage';
import { SCANS_PAGE_REFRESH_INTERVAL_MS } from '../../scansPageConstants';
import { selectUploadSortOrder } from '../../scansSlice';
import { sortCustomerUploadsSummary } from '../utils/sortCustomerUploadsSummary';
import { UploadListSkeleton } from './UploadListSkeleton';
import { UploadSummaryListItem } from './UploadSummaryListItem';

export const UploadSummaryList = () => {
  const uploadOrder = useAppSelector(selectUploadSortOrder);
  const [expandedCustomerIds, setExpandedCustomerIds] = useState<Set<string>>(
    new Set()
  );

  // Manage the expanded state of customers at the parent level to preserve their state across data re-fetches. This ensures frequent refreshes without collapsing all expanded items, improving user experience.
  const handleToggleExpanded = useCallback(
    (customerId: string, isExpanded: boolean) => {
      setExpandedCustomerIds((prevIds) => {
        const newIds = new Set(prevIds);
        if (isExpanded) {
          newIds.add(customerId);
        } else {
          newIds.delete(customerId);
        }
        return newIds;
      });
    },
    []
  );

  const { currentData, isLoading, isFetching } = useGetUploadSummariesQuery(
    undefined,
    {
      pollingInterval: SCANS_PAGE_REFRESH_INTERVAL_MS,
    }
  );

  const sortedCustomerUploadsSummaries = useMemo(() => {
    if (currentData) {
      return sortCustomerUploadsSummary(uploadOrder, currentData);
    }
  }, [currentData, uploadOrder]);

  const uploadList = useMemo(
    () => (
      <Stack
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
        }}
      >
        {sortedCustomerUploadsSummaries?.map((customerSummary, idx) => (
          <UploadSummaryListItem
            summary={customerSummary}
            key={`${customerSummary.customerID}-${idx}`}
            isExpanded={expandedCustomerIds.has(customerSummary.customerID)}
            onExpandedChange={handleToggleExpanded}
          />
        ))}
      </Stack>
    ),
    [sortedCustomerUploadsSummaries, expandedCustomerIds, handleToggleExpanded]
  );

  let content;
  if (isLoading || isFetching) {
    content = <UploadListSkeleton />;
  } else if (
    !sortedCustomerUploadsSummaries ||
    sortedCustomerUploadsSummaries.length === 0
  ) {
    content = (
      <CenteredBox>
        <NoDataMessage message='No active uploads or processing' />
      </CenteredBox>
    );
  } else {
    content = uploadList;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {content}
    </Box>
  );
};

/**
 * CenteredBox - A Box component that centers its children both horizontally and vertically.
 * Spans the full width and height of its container by default.
 */
const CenteredBox = styled('div')(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  m: '1.5rem 0',
}));
